import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Download, Play } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useFleetStore } from '@/stores/fleet';
import { cn } from '@/lib/utils';

export type Preset = 'today' | 'yesterday' | '7d' | '30d';

export function rangeForPreset(preset: Preset): { from: string; to: string } {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case 'today':
      return { from: startOfToday.toISOString(), to: now.toISOString() };
    case 'yesterday': {
      const start = new Date(startOfToday.getTime() - 24 * 60 * 60 * 1000);
      return { from: start.toISOString(), to: startOfToday.toISOString() };
    }
    case '7d': {
      const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return { from: start.toISOString(), to: now.toISOString() };
    }
    case '30d':
    default: {
      const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return { from: start.toISOString(), to: now.toISOString() };
    }
  }
}

export const PRESET_LABEL: Record<Preset, string> = {
  today: 'Hoje',
  yesterday: 'Ontem',
  '7d': 'Últimos 7 dias',
  '30d': 'Últimos 30 dias',
};

/**
 * Snapshot dos filtros que foi efetivamente "submetido" pra geração do
 * relatório. A query só executa quando isso é não-nulo.
 */
export interface ExecutedFilters {
  deviceId: number | 'all' | null;
  ids: number[];
  preset: Preset;
  range: { from: string; to: string };
}

/**
 * Gerencia os filtros e o ciclo de "preencher → gerar". A request só
 * dispara quando `executed` muda (após `generate()`); mudar filtros sem
 * clicar não dispara nada.
 */
export function useReportFilters(deviceMode: 'single' | 'multi') {
  const devices = useFleetStore((s) => s.devices);
  const sortedDevices = useMemo(
    () => Array.from(devices.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [devices],
  );

  const initialDevice =
    deviceMode === 'single' ? (sortedDevices[0]?.id ?? null) : ('all' as const);
  const [deviceId, setDeviceId] = useState<number | 'all' | null>(initialDevice);
  const [preset, setPreset] = useState<Preset>('today');
  const [executed, setExecuted] = useState<ExecutedFilters | null>(null);
  const range = useMemo(() => rangeForPreset(preset), [preset]);

  if (deviceMode === 'single' && deviceId === null && sortedDevices[0]) {
    setDeviceId(sortedDevices[0].id);
  }

  const generate = () => {
    const ids =
      deviceId === 'all'
        ? sortedDevices.map((d) => d.id)
        : typeof deviceId === 'number'
          ? [deviceId]
          : [];
    if (ids.length === 0) return;
    setExecuted({ deviceId, ids, preset, range: rangeForPreset(preset) });
  };

  return {
    devices,
    sortedDevices,
    deviceId,
    setDeviceId,
    preset,
    setPreset,
    range,
    executed,
    generate,
  };
}

interface ShellProps {
  title: string;
  description?: string;
  children: ReactNode;
}

export function ReportShell({ title, description, children }: ShellProps) {
  return (
    <div className="space-y-4 p-4 sm:p-6">
      <Link
        to="/reports"
        className="-ml-2 inline-flex items-center gap-0.5 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-primary/5"
      >
        <ChevronLeft className="h-5 w-5" />
        Voltar
      </Link>
      <div>
        <h1 className="text-xl font-bold">{title}</h1>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {children}
    </div>
  );
}

interface FiltersProps {
  deviceMode: 'single' | 'multi';
  deviceId: number | 'all' | null;
  onDeviceChange: (v: number | 'all') => void;
  preset: Preset;
  onPresetChange: (p: Preset) => void;
  onGenerate: () => void;
  generating?: boolean;
  extra?: ReactNode;
}

export function ReportFilters({
  deviceMode,
  deviceId,
  onDeviceChange,
  preset,
  onPresetChange,
  onGenerate,
  generating,
  extra,
}: FiltersProps) {
  const devices = useFleetStore((s) => s.devices);
  const sorted = useMemo(
    () => Array.from(devices.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [devices],
  );
  const canGenerate =
    deviceMode === 'multi' ? sorted.length > 0 : typeof deviceId === 'number';

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className={cn('grid gap-3', extra ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
          <label className="space-y-1">
            <span className="text-xs font-semibold text-muted-foreground">Máquina</span>
            <select
              value={deviceId === null ? '' : String(deviceId)}
              onChange={(e) =>
                onDeviceChange(e.target.value === 'all' ? 'all' : Number(e.target.value))
              }
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              {deviceMode === 'multi' && <option value="all">Todas</option>}
              {sorted.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-xs font-semibold text-muted-foreground">Período</span>
            <select
              value={preset}
              onChange={(e) => onPresetChange(e.target.value as Preset)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              {Object.entries(PRESET_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          {extra}
        </div>
        <button
          type="button"
          onClick={onGenerate}
          disabled={!canGenerate || generating}
          className={cn(
            'inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5',
            'text-sm font-semibold text-primary-foreground shadow-sm transition-colors',
            'hover:bg-primary/90 disabled:opacity-50 sm:w-auto',
          )}
        >
          <Play className="h-4 w-4" />
          {generating ? 'Gerando…' : 'Gerar Relatório'}
        </button>
      </CardContent>
    </Card>
  );
}

interface ResultBarProps {
  loading: boolean;
  count: number;
  unit?: string;
  onExport: () => void;
  disabled?: boolean;
}

export function ResultBar({ loading, count, unit = 'linhas', onExport, disabled }: ResultBarProps) {
  return (
    <div className="flex items-center justify-between">
      <p className="text-sm text-muted-foreground">
        {loading
          ? 'Carregando…'
          : `${count.toLocaleString('pt-BR')} ${count === 1 ? unit.replace(/s$/, '') : unit}`}
      </p>
      <button
        type="button"
        onClick={onExport}
        disabled={disabled || loading || count === 0}
        className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent disabled:opacity-50"
      >
        <Download className="h-3.5 w-3.5" />
        Exportar CSV
      </button>
    </div>
  );
}

export function exportCsv(filename: string, header: string[], rows: (string | number | null | undefined)[][]) {
  const escape = (cell: string | number | null | undefined) => {
    const s = cell == null ? '' : String(cell);
    return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map((r) => r.map(escape).join(';')).join('\n');
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function ReportTable({
  loading,
  error,
  empty,
  notRun,
  children,
}: {
  loading: boolean;
  error: unknown;
  empty: boolean;
  notRun: boolean;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-0">
        {notRun ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            Selecione os filtros e clique em <strong className="font-semibold">Gerar Relatório</strong>.
          </p>
        ) : loading ? (
          <p className="p-6 text-sm text-muted-foreground">Carregando…</p>
        ) : error ? (
          <p className="p-6 text-sm text-destructive">Erro ao carregar relatório.</p>
        ) : empty ? (
          <p className="p-6 text-sm text-muted-foreground">Sem dados no período.</p>
        ) : (
          <div className="overflow-x-auto">{children}</div>
        )}
      </CardContent>
    </Card>
  );
}

export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0min';
  const totalMin = Math.floor(ms / 60000);
  const days = Math.floor(totalMin / (60 * 24));
  const hours = Math.floor((totalMin - days * 24 * 60) / 60);
  const minutes = totalMin - days * 24 * 60 - hours * 60;
  if (days > 0) return `${days}d ${hours}h ${String(minutes).padStart(2, '0')}min`;
  return `${hours}h ${String(minutes).padStart(2, '0')}min`;
}

export function metersToKm(m?: number | null): string {
  if (m == null || !Number.isFinite(m)) return '—';
  return `${(m / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} km`;
}

export function knotsToKmh(k?: number | null): string {
  if (k == null || !Number.isFinite(k)) return '—';
  return `${Math.round(k * 1.852)} km/h`;
}
